/**
 * query-cache.ts — Global query result cache with stale-while-revalidate
 *
 * Inspired by SWR/React Query but lightweight (~100 LOC, no deps).
 * Each query key maps to a cached result + timestamp.
 * - Fresh: return cache, skip fetch
 * - Stale: return cache immediately, refetch in background
 * - Expired: fetch and block
 *
 * @see Issue #618
 */
import { createLogger } from './logger';

const log = createLogger('QueryCache');

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  /** Promise of in-flight revalidation (deduplication) */
  revalidating?: Promise<T>;
}

const cache = new Map<string, CacheEntry<unknown>>();

/** Default: data is fresh for 30s, stale for 5min, then expired */
export interface CacheConfig {
  /** Milliseconds before data is considered stale (default 30s) */
  freshMs: number;
  /** Milliseconds before data is evicted entirely (default 5min) */
  maxAgeMs: number;
}

const DEFAULT_CONFIG: CacheConfig = {
  freshMs: 30_000,
  maxAgeMs: 300_000,
};

type CacheStatus = 'fresh' | 'stale' | 'miss';

function getStatus(key: string, config: CacheConfig): CacheStatus {
  const entry = cache.get(key);
  if (!entry) return 'miss';
  const age = Date.now() - entry.timestamp;
  if (age < config.freshMs) return 'fresh';
  if (age < config.maxAgeMs) return 'stale';
  cache.delete(key);
  return 'miss';
}

/**
 * Fetch with stale-while-revalidate semantics.
 *
 * @param key  Unique cache key (e.g. "topics:20:0")
 * @param fetcher  Async function that returns data
 * @param config  Cache timing configuration
 * @returns  Data (possibly stale)
 */
export async function cachedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  config: CacheConfig = DEFAULT_CONFIG,
): Promise<T> {
  const status = getStatus(key, config);
  const entry = cache.get(key) as CacheEntry<T> | undefined;

  if (status === 'fresh' && entry) {
    return entry.data;
  }

  if (status === 'stale' && entry) {
    // Return stale data immediately, revalidate in background
    if (!entry.revalidating) {
      entry.revalidating = fetcher().then((data) => {
        cache.set(key, { data, timestamp: Date.now() });
        return data;
      }).catch((err) => {
        log.warn('Background revalidation failed', { key, err: err?.message });
        return entry.data; // Keep stale data on error
      }).finally(() => {
        const e = cache.get(key) as CacheEntry<T> | undefined;
        if (e) e.revalidating = undefined;
      });
    }
    return entry.data;
  }

  // Cache miss — fetch and wait
  // Deduplicate: if another caller already started fetching, reuse
  if (entry?.revalidating) {
    return entry.revalidating;
  }

  const promise = fetcher().then((data) => {
    cache.set(key, { data, timestamp: Date.now() });
    return data;
  }).catch((err) => {
    // #699: Clean up failed cache entry to avoid stale undefined data
    cache.delete(key);
    throw err;
  });

  // Store the in-flight promise for deduplication
  cache.set(key, { data: undefined as T, timestamp: 0, revalidating: promise });

  return promise;
}

/**
 * Pre-warm a cache key without blocking.
 * Fire-and-forget: starts fetch in background.
 */
export function prefetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  config: CacheConfig = DEFAULT_CONFIG,
): void {
  const status = getStatus(key, config);
  if (status === 'fresh') return; // Already cached
  cachedFetch(key, fetcher, config).catch(() => {});
}

/**
 * Read cache synchronously (for initial render).
 * Returns undefined if not cached or expired.
 */
export function readCache<T>(key: string, config: CacheConfig = DEFAULT_CONFIG): T | undefined {
  const status = getStatus(key, config);
  if (status === 'miss') return undefined;
  return (cache.get(key) as CacheEntry<T>)?.data;
}

/** Invalidate a specific key or all keys matching a prefix */
export function invalidateCache(keyOrPrefix: string): void {
  if (cache.has(keyOrPrefix)) {
    cache.delete(keyOrPrefix);
    return;
  }
  // Prefix invalidation
  for (const k of cache.keys()) {
    if (k.startsWith(keyOrPrefix)) cache.delete(k);
  }
}

/** Clear all cached data */
export function clearCache(): void {
  cache.clear();
}
